import { useEffect, useState, useCallback } from 'react';
import { Compass, Loader2, Wand2, AlertTriangle, Check } from 'lucide-react';
import { AdminPageHeader, StatCard, useFetcher, API } from './_P1Common';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';

const PROBLEM_LABELS = {
  missing_image: 'Nedostaje glavna slika',
  short_meta: 'Meta opis prekratak (<120)',
  long_meta: 'Meta opis predug (>160)',
  short_title: 'Naslov prekratak (<20)',
};

export default function AdminAMPDiscover() {
  const { data: summary, loading: sumLoading } = useFetcher('amp-discover');
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setListLoading(true);
    const r = await authFetch(`${API}/admin/amp-discover/articles?only_problems=${onlyProblems ? 'true' : 'false'}&limit=500`);
    if (r.ok) {
      const d = await r.json();
      setItems(d.items || []);
    }
    setListLoading(false);
  }, [authFetch, onlyProblems]);

  useEffect(() => { load(); }, [load]);

  const toggleSel = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selIds = Object.keys(selected).filter(k => selected[k]);
  const allSelected = items.length > 0 && items.every(i => selected[i.id]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const next = {};
      items.forEach(i => { next[i.id] = true; });
      setSelected(next);
    }
  };

  const fixSelected = async () => {
    if (selIds.length === 0) return;
    setBusy(true);
    try {
      const r = await authFetch(`${API}/admin/amp-discover/fix`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ article_ids: selIds }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Popravljeno ${d.fixed} članaka`);
        setSelected({});
        load();
      } else toast.error('Greška pri popravku');
    } finally { setBusy(false); }
  };

  const fixAll = async () => {
    if (!window.confirm('Popraviti sve članke s problemima? (meta opis se automatski generira iz sažetka)')) return;
    setBusy(true);
    try {
      const r = await authFetch(`${API}/admin/amp-discover/fix`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ all_with_problems: true, limit: 1000 }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Masovno popravljeno: ${d.fixed} članaka (preskočeno bez sažetka: ${d.skipped_no_excerpt})`);
        load();
      } else toast.error('Greška pri masovnom popravku');
    } finally { setBusy(false); }
  };

  if (sumLoading || !summary) return <AdminPageHeader icon={Compass} title="AMP i Google Discover"><Loader2 className="animate-spin"/></AdminPageHeader>;

  return (
    <div data-testid="admin-amp-discover">
      <AdminPageHeader icon={Compass} title="AMP i Google Discover" subtitle="Analiza, označavanje i bulk popravak AMP/Discover problema"/>
      <div className="px-4 md:px-6 grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard testid="amp-total" label="Objavljenih" value={summary.total_published} accent="blue"/>
        <StatCard testid="amp-ready" label="AMP spremnih" value={summary.amp_ready} accent="green"/>
        <StatCard testid="amp-coverage" label="Pokrivenost" value={`${summary.coverage_pct}%`} accent={summary.coverage_pct >= 80 ? 'green' : 'amber'}/>
        <StatCard testid="amp-discover" label="Discover kvalificirano" value={summary.discover_eligible} accent="violet"/>
      </div>

      <div className="px-4 md:px-6 flex flex-wrap items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)}
                 data-testid="toggle-only-problems" className="accent-red-600" />
          Prikaži samo članke s problemima
        </label>
        <div className="ml-auto flex gap-2">
          <button onClick={fixSelected} disabled={busy || selIds.length === 0}
                  data-testid="fix-selected-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-sm disabled:opacity-40">
            <Wand2 size={14}/> Popravi odabrane ({selIds.length})
          </button>
          <button onClick={fixAll} disabled={busy}
                  data-testid="fix-all-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-bold disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>}
            Popravi sve s problemima
          </button>
        </div>
      </div>

      <div className="px-4 md:px-6 pb-6">
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <table className="min-w-full text-sm" data-testid="amp-articles-table">
            <thead className="bg-zinc-900 text-xs text-zinc-400 uppercase">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                         data-testid="amp-select-all" className="accent-red-600" />
                </th>
                <th className="px-3 py-2 text-left">Članak</th>
                <th className="px-3 py-2 text-center w-24">Slika</th>
                <th className="px-3 py-2 text-center w-24">Meta (znak.)</th>
                <th className="px-3 py-2 text-left">Problemi</th>
                <th className="px-3 py-2 w-16">Status</th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center"><Loader2 className="animate-spin mx-auto text-zinc-500"/></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">
                  {onlyProblems ? 'Nema članaka s AMP/Discover problemima — sve je u redu.' : 'Nema članaka.'}
                </td></tr>
              ) : items.map(a => (
                <tr key={a.id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`amp-row-${a.id}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={!!selected[a.id]} onChange={() => toggleSel(a.id)}
                           className="accent-red-600" data-testid={`amp-sel-${a.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <a href={`/clanak/${a.slug}`} target="_blank" rel="noreferrer" className="text-zinc-200 hover:text-white font-medium">{a.title_hr}</a>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {a.has_image
                      ? <Check size={14} className="inline text-emerald-500"/>
                      : <AlertTriangle size={14} className="inline text-amber-500"/>}
                  </td>
                  <td className="px-3 py-2 text-center text-xs">{a.meta_length}</td>
                  <td className="px-3 py-2">
                    {a.problems.length === 0 ? (
                      <span className="text-emerald-400 text-xs">Sve u redu</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {a.problems.map(p => (
                          <span key={p} className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded">
                            {PROBLEM_LABELS[p] || p}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {a.amp_ready
                      ? <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded">READY</span>
                      : <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded">FIX</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-zinc-500 mt-3 max-w-2xl">
          Automatski popravak regenerira <code>meta_description_hr</code> iz sažetka članka (ciljano 120–160 znakova). Članke bez sažetka i bez slike mora se ručno popraviti kroz <a href="/admin/articles" className="text-red-400 hover:underline">Članci</a>.
        </p>
      </div>
    </div>
  );
}
